import { ImageName } from "@mcleod/images";
import { CommonDialogs } from "@mcleod/common";
import { Button, DataSourceMode, Label, Layout, Panel, SlideoutDecorator, Snackbar, Toast } from "@mcleod/components";
import { Api, HorizontalAlignment, LogManager, ProcessLock, StringUtil } from "@mcleod/core";
import { ModelOrders, OrderTabView } from "@mcleod/dispatch";
import { CompareOrder } from "./CompareOrder";
import { RowEdiOrder } from "./models/ModelEdiOrder";
import { ViewEdiOrder } from "./ViewEdiOrder";

const log = LogManager.getLogger("lme.datafusion.CompareTender");
const LOCK_NAME = "DataFusion compare";

export class CompareTender {
    private _applyButtonClicked: boolean = false;
    private _appliedChanges: number = 0;
    private _movementId: string = null;

    async compareTender(ediOrderRow: RowEdiOrder, needsReply: boolean, VEO?: ViewEdiOrder, orderIdOverride?: string): Promise<any> {
        return new Promise(async (resolve, reject) => {
            const tenderId = ediOrderRow.get("id");
            const orderId = orderIdOverride || ediOrderRow.get("order_id");
            let allowReturn: boolean = false;

            // Validate required parameters
            if (!tenderId) {
                log.error("Tender ID is required to compare tender.");
                reject("Tender ID is required to compare tender.");
                return;
            }

            if (!orderId) {
                log.error("Order ID is required to compare tender.");
                reject("Order ID is required to compare tender.");
                return;
            }

            // Fetch order to get movement ID for locking
            const orderRow = await new ModelOrders().searchSingle({ id: orderId }, "lme/dispatch/Orders");
            this._movementId = orderRow?.get("curr_movement_id", null);

            if (!this._movementId) {
                log.error("Movement ID could not be determined for order " + orderId);
                reject("Movement ID could not be determined for order " + orderId);
                return;
            }

            const movementLocked = await Api.search("lme/datafusion/check-movement-lock", { movement_id: this._movementId });
            if (movementLocked.data[0].is_locked) {
                reject(movementLocked.data[0].details);
                return;
            }

            // Acquire lock before proceeding with compare
            ProcessLock.acquireLock(LOCK_NAME, this._movementId).then(async lockResult => {
                if (lockResult.success !== true) {
                    return;
                }

                await Api.search("lme/datafusion/compare-order", { edi_order_id: tenderId, order_id: orderId }).then(async result => {
                    if (result.data[0].no_changes) {
                        ediOrderRow.set("order_compared", "Y");
                        ediOrderRow.post().then(async result => {
                            if (needsReply) {
                                CommonDialogs.showYesNo("No changes were found.  Would you still like to accept this tender?", "Reply to Tender",
                                    {
                                        titleProps: {
                                            imageName: ImageName.circleX,
                                            imageColor: "primary.light"
                                        }
                                    }).then(async yes => {
                                        if (yes) {
                                            await this.acceptLoadTender(tenderId).then(response => {
                                                this.processReplyStatus(orderId, response, "not compared - no changes");
                                            }).catch(reason => {
                                                log.info("Could not reply.  " + reason);
                                            });
                                            allowReturn = true;
                                        } else {
                                            // Always unlock when user says no to accepting tender with no changes
                                            await this.unlockTender(ediOrderRow);
                                        }
                                        await this.releaseLock();
                                    });
                            }else{
                                this.unlockTender(ediOrderRow);
                                await this.releaseLock();
                                CommonDialogs.showMessage("No changes were found.");
                            }
                        });
                    } else {
                        const compareLayout = Layout.getLayout("lme/datafusion/CompareOrder") as CompareOrder;
                        compareLayout.isOrderUpdateAllowed = result.data[0].is_order_update_allowed;
                        compareLayout.loadFromApiData(result.data[0], DataSourceMode.ADD);
                        compareLayout.orderId = orderId;
                        compareLayout.tenderId = tenderId;

                        const btnApplyChanges = new Button({ caption: "Apply Changes", rowBreak: false, backgroundColor: "primary", color: "McLeodWhite" });
                        btnApplyChanges.addClickListener(async event => {
                            this._applyButtonClicked = true;
                            btnApplyChanges.busy = true;
                            this._appliedChanges = compareLayout.applyChanges(btnApplyChanges);

                        });

                        const comparePanel = this.getCompareSlideOutDecorator(compareLayout, orderId, btnApplyChanges, tenderId, ediOrderRow, needsReply, VEO);
                        compareLayout.buttonApply = btnApplyChanges;
                        (await comparePanel).addUnmountListener(async () => {
                            this.unlockTender(ediOrderRow);
                        });
                    }
                }).then(result => {
                    resolve(result)
                }).catch(async error => {
                    await this.releaseLock();
                    reject(error);
                });
            }).catch((error) => {
                CommonDialogs.showError(error, "Compare Tender Lock");
                reject(error);
            });
        });
    }

    getCompareSlideOutDecorator(compareLayout: CompareOrder, orderId: string, btnApplyChanges: Button, tenderId: string, ediOrderRow: RowEdiOrder, needsReply: boolean, VEO?: ViewEdiOrder): Promise<SlideoutDecorator> {
        return new Promise(async (resolve, reject) => {
            const compareSOD = new SlideoutDecorator({
                layout: compareLayout,
                title: "Compare Order - " + orderId,
                fillVerticalSpace: true,
                fillHorizontalSpace: false,
                width: window.innerWidth * 0.95,
                doAfterSlideIn: (decorator: SlideoutDecorator) => {
                    compareLayout.insert(btnApplyChanges, 3);
                    compareLayout.buttonSelectAll.visible = compareLayout.isOrderUpdateAllowed;
                    compareLayout.buttonDeselectAll.visible = compareLayout.isOrderUpdateAllowed;
                },
                doAfterSlideOut: async (decorator: SlideoutDecorator) => {
                    try {
                        if (compareLayout.changesApplied > 0) { //changes have been applied, process reply
                            this.acceptLoadTender(tenderId).then(res => {
                                const batch = res.data[0].reply_batch;
                                const desc = res.data[0].description;
                                if (batch != "-1" && batch != "0" && desc != "Tender does not require a reply") {
                                    Toast.showToast(this.getResultsToastPanel(orderId, desc, "compared"), null, { millisUntilDismissal: 7000 });
                                } else if (batch == "0" && desc == "Tender does not require a reply") {
                                    Toast.showToast(this.getResultsToastPanel(orderId, desc, "sucessfully updated"), null, { millisUntilDismissal: 7000 });
                                } else {
                                    Snackbar.showWarningSnackbar("Unable to send reply, but order " + orderId + " was successfully updated by the compare process", { millisUntilDismissal: 7000 });
                                }
                            }).catch(reason => {
                                log.info("Could not reply.  " + reason);
                                Snackbar.showWarningSnackbar("Unable to send reply [" + reason + "], but order " + orderId + " was successfully updated by the compare process", { millisUntilDismissal: 7000 });
                            }).finally(() => {
                                if (ediOrderRow.get("order_compared") == "N") {
                                    ediOrderRow.set("order_compared", "Y");
                                }
                                this.delay(2000).then(() => {
                                    if (VEO) {
                                        VEO.mainDataSource.search({ id: tenderId });
                                    }
                                });
                            });
                        } else if (!this._applyButtonClicked || compareLayout.changesApplied == 0) { //either button was not clicked or no changes accepted
                            if (needsReply) {
                                CommonDialogs.showYesNo("Compare Not Performed or no changes applied.  Would you still like to accept this tender?", "Reply to Tender",
                                    {
                                        titleProps: {
                                            imageName: ImageName.circleX,
                                            imageColor: "primary.light"
                                        }
                                    }).then(async yes => {
                                        if (yes) {
                                            await this.acceptLoadTender(tenderId).then(response => {
                                                const batch = response.data[0].reply_batch;
                                                const desc = response.data[0].description;
                                                if (batch != "-1" && batch != "0" && desc != "Tender does not require a reply") {
                                                    Toast.showToast(this.getResultsToastPanel(orderId, desc, "sucessfully updated"), null, { millisUntilDismissal: 7000 });
                                                } else if (batch == "-1" && desc != "Tender does not require a reply") {
                                                    Snackbar.showWarningSnackbar("Unable to send reply, but order " + orderId + " was successfully updated by the compare process", { millisUntilDismissal: 7000 });
                                                }
                                                if (VEO) {
                                                    VEO.mainDataSource.search({ id: tenderId });
                                                }
                                            });
                                        } else {
                                            // Always unlock when user says no to accepting tender
                                            await this.unlockTender(ediOrderRow);
                                        }

                                        if ((!this._applyButtonClicked) && (ediOrderRow.get("no_display") === "N")) { //give user option to exclude tender
                                            CommonDialogs.showYesNo("Would you like to remove this tender from Tender Express (exclude)?").then(async response => {
                                                if (response || ediOrderRow.get("record_in_use") === "Y") {
                                                    if (response) {
                                                        ediOrderRow.set("no_display", "Y");
                                                    }
                                                    if (ediOrderRow.get("record_in_use") === "Y") {
                                                        ediOrderRow.set("record_in_use", "N");
                                                    }
                                                    await ediOrderRow.post();
                                                }
                                            });
                                        }
                                    });
                            }
                            else {
                                if ((!this._applyButtonClicked) && (ediOrderRow.get("no_display") === "N")) { //give user option to exclude tender
                                    CommonDialogs.showYesNo("Would you like to remove this tender from Tender Express (exclude)?").then(async response => {
                                        if (response) {
                                            ediOrderRow.set("no_display", "Y");
                                            ediOrderRow.set("record_in_use", "N");
                                            await ediOrderRow.post()
                                        }
                                        else {
                                            this.unlockTender(ediOrderRow);
                                        }
                                    });
                                }
                            }
                        }
                    } finally {
                        await this.releaseLock();
                    }
                    resolve(compareSOD);
                    // this.delay(2000).then(()=>{
                    //   log.info("Delay done.  Refreshing.");
                    // });
                }
            });
        });
    }

    processReplyStatus(orderId: any, response: any, arg2: string) {
        throw new Error("Method not implemented.");
    }

    async unlockTender(ediOrderRow: RowEdiOrder) {
        const locked = ediOrderRow.get("record_in_use", "N");
        if (locked === "Y") {
            ediOrderRow.set("record_in_use", "N");
            await ediOrderRow.post();
        }
    }

    async releaseLock() {
        if (this._movementId) {
            await ProcessLock.releaseLock(LOCK_NAME, this._movementId);
        }
    }

    async acceptLoadTender(ediOrderId: string): Promise<any> {
        return new Promise(async (resolve) => {
            resolve(await this.sendReply(ediOrderId));
        });
    }

    async sendReply(ediOrderId: string): Promise<any> {
        log.info("Replying to tender " + ediOrderId + ".")
        return await Api.post("lme/datafusion/accept-tender", {
            edi_order_id: ediOrderId
        });
    }

    getResultsToastPanel(orderId: string, message: string, action: string): Panel {
        const labelProps = { color: "white", fontSize: "xlarge", rowBreak: false, paddingLeft: 3, paddingRight: 1, marginLeft: 0, marginRight: 0, wrap: false };
        const labelLinkProps = { ...labelProps, style: { textDecoration: "underline" } };

        if (!StringUtil.isEmptyString(orderId)) {
            return new Panel({
                align: HorizontalAlignment.CENTER, fillRow: true,
                components: [
                    new Label({ ...labelProps, text: "Order " }),
                    new Label({ ...labelLinkProps, text: orderId, onClick: event => OrderTabView.navigateTo({ key: orderId, newTab: true }) }),
                    new Label({ ...labelProps, text: " " + action + ".  " + message })
                ]
            });
        }
        else {
            return new Panel({
                align: HorizontalAlignment.CENTER, fillRow: true,
                components: [
                    new Label({ ...labelProps, text: "Order not " + action + ".  " + message })
                ]
            });
        }
    }

    async delay(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}
